import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronRight,
  Filter,
  Info,
  RotateCcw,
  Search,
  Trash,
  X,
} from "./advancedSearchIcons";

const iconSize = 16;

export const isAdvancedSearchValueActive = (value) => {
  if (Array.isArray(value)) {
    return (
      value.filter((item) => item !== "" && item !== null && item !== undefined)
        .length > 0
    );
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (value && typeof value === "object") {
    return Object.values(value).some(isAdvancedSearchValueActive);
  }
  if (value === 0) {
    return true;
  }
  return String(value ?? "").trim().length > 0;
};

export function AdvancedSearchField({
  label,
  icon: Icon = Filter,
  active = false,
  required = false,
  selected = false,
  onClick,
  children,
  hint,
}) {
  return (
    <div
      className={`mb2 pa2 br2 ba pointer ${
        selected ? "bordoColAlt sfondoColChiaro" : "b--transparent"
      }`}
      style={{
        backgroundColor: required
          ? "rgba(185, 28, 28, 0.09)"
          : active ? "rgba(19, 119, 82, 0.09)" : undefined,
        borderColor: required
          ? "rgba(185, 28, 28, 0.6)"
          : active ? "rgba(19, 119, 82, 0.35)" : undefined,
      }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Icon size={iconSize} className="mr2 scrittaColAltScuro" />
          <span className={`f7 ttu tracked b ${required ? "dark-red" : "gray"}`}>{label}</span>
        </div>
        {onClick && <ChevronRight size={14} className="gray" />}
      </div>
      {children && (
        <div className="mt2" onClick={(event) => event.stopPropagation()}>
          {children}
        </div>
      )}
      {hint && <div className="mt2 f7 gray lh-copy">{hint}</div>}
    </div>
  );
}

const advancedSearchRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "0.75rem",
  alignItems: "start",
};

export function AdvancedSearchFieldRow({ children }) {
  return (
    <div className="mb2" style={advancedSearchRowStyle}>
      {children}
    </div>
  );
}

export function AdvancedSearchTextInput(props) {
  const active = isAdvancedSearchValueActive(props.value);
  return (
    <input
      {...props}
      className={`input-reset ba br2 pa2 w-100 ${props.className ?? ""}`}
      style={{
        borderColor: active ? "rgba(19, 119, 82, 0.5)" : "#d1d5db",
        backgroundColor: active ? "rgba(19, 119, 82, 0.08)" : "#fff",
        ...(props.style ?? {}),
      }}
    />
  );
}

export function AdvancedSearchSelect({ children, value, ...props }) {
  const active = isAdvancedSearchValueActive(value);
  return (
    <select
      {...props}
      value={value}
      className={`input-reset ba br2 pa2 w-100 ${props.className ?? ""}`}
      style={{
        borderColor: active ? "rgba(19, 119, 82, 0.5)" : "#d1d5db",
        backgroundColor: active ? "rgba(19, 119, 82, 0.08)" : "#fff",
        ...(props.style ?? {}),
      }}
    >
      {children}
    </select>
  );
}

export function AdvancedSearchLayout({
  title = "Ricerca avanzata",
  height = "90vh",
  tabs,
  activeTab,
  onTabChange,
  filterColumn,
  valueColumn,
  valueColumnWide = false,
  summaryEntries = [],
  summaryExtra,
  showSummaryColumn = true,
  footerCenter,
  footerLeftExtra,
  onClose,
  onReset,
  onRemoveSummaryEntry,
  onApply,
  applyLabel = "Applica filtri",
  resetLabel = "Reimposta",
  closeLabel = "Chiudi",
  children,
}) {
  return (
    <div
      className="w-100 h-100 flex flex-column sfondoBiancoTema"
      style={{ height, maxHeight: height, minHeight: 0, overflow: "hidden" }}
    >
      <div className="flex-shrink-0 flex items-center justify-between pa3 bb bordoColAlt">
        <div className="flex items-center">
          <Filter size={20} className="mr2 scrittaColAltScuro" />
          <div className="f4 fw5">{title}</div>
        </div>
        {onClose && (
          <button
            type="button"
            className="button-reset bg-transparent bn pointer pa2 br2"
            onClick={onClose}
            aria-label={closeLabel}
            title={closeLabel}
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div
        className="flex flex-column flex-row-l flex-auto overflow-hidden"
        style={{ minHeight: 0 }}
      >
        <nav
          className="w-100 w-20-l pa3 br-l bordoColAlt"
          style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon ?? Filter;
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                className="button-reset bg-transparent bn pointer w-100 mb2 pa2 br2 tl flex items-center justify-between"
                style={{
                  backgroundColor: selected
                    ? "var(--colAltChiaro, rgba(0,0,0,0.06))"
                    : undefined,
                }}
                onClick={() => onTabChange(tab.key)}
              >
                <span className="flex items-center">
                  <Icon size={iconSize} className="mr2 scrittaColAltScuro" />
                  <span className="f6">{tab.label}</span>
                </span>
                <span className="flex items-center">
                  {tab.activeCount > 0 && (
                    <span
                      className="br-pill white f7 b flex items-center justify-center ml1"
                      style={{
                        minWidth: "1.35rem",
                        height: "1.35rem",
                        backgroundColor: "var(--colTastoVerde, #137752)",
                      }}
                    >
                      {tab.activeCount}
                    </span>
                  )}
                  {tab.requiredCount > 0 && (
                    <span
                      className="br-pill white f7 b flex items-center justify-center ml1"
                      style={{
                        minWidth: "1.35rem",
                        height: "1.35rem",
                        backgroundColor: "#b91c1c",
                      }}
                      title="Dati richiesti da integrare"
                    >
                      {tab.requiredCount}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </nav>

        <section
          className="w-100 w-30-l pa3 br-l bordoColAlt"
          style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          {filterColumn}
        </section>

        <section
          className={`${valueColumnWide ? "w-100 w-50-l" : "w-100 w-30-l"} pa3 ${showSummaryColumn ? "br-l bordoColAlt" : ""}`}
          style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          {valueColumn ?? (
            <div className="pa3 br2 ba b--black-10 gray lh-copy">
              Seleziona un filtro con valori predefiniti per modificarli qui.
            </div>
          )}
        </section>

        {showSummaryColumn && (
        <aside
          className="w-100 w-20-l pa3"
          style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
        >
          <div className="flex items-center mb3">
            <Check size={iconSize} className="mr2 scrittaColAltScuro" />
            <div className="f6 fw6">Filtri attivi</div>
          </div>
          {summaryEntries.length === 0 ? (
            <div className="f7 gray lh-copy">Nessun filtro valorizzato.</div>
          ) : (
            <div>
              {summaryEntries.map(([label, value, removeKey], index) => {
                const content = (
                  <>
                    <div className="f7 ttu tracked gray">{label}</div>
                    <div
                      className="f6 b lh-copy"
                      style={{ wordBreak: "break-word" }}
                    >
                      {value}
                    </div>
                  </>
                );
                const commonStyle = { backgroundColor: "rgba(19, 119, 82, 0.08)" };
                if (removeKey && onRemoveSummaryEntry) {
                  return (
                    <button
                      key={label + "-" + index}
                      type="button"
                      className="button-reset tl w-100 mb2 pa2 br2 pointer flex items-start justify-between"
                      style={commonStyle}
                      title="Rimuovi filtro"
                      onClick={() => onRemoveSummaryEntry(removeKey)}
                    >
                      <span className="pr2 flex-auto">{content}</span>
                      <Trash size={15} className="gray flex-shrink-0 mt1" />
                    </button>
                  );
                }
                return (
                  <div
                    key={label + "-" + index}
                    className="mb2 pa2 br2"
                    style={commonStyle}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          )}
          {summaryExtra && <div className="mt3 pt3 bt b--black-10">{summaryExtra}</div>}
        </aside>
        )}
      </div>

      {children}

      <div className="flex-shrink-0 pa3 bt bordoColAlt flex flex-wrap items-center justify-between">
        <div className="flex items-center">
          {footerLeftExtra}
          {onReset && (
            <button
              type="button"
              className="dim tastoInputOrizzontale br3 mr2 inline-flex items-center"
              onClick={onReset}
            >
              <RotateCcw size={iconSize} className="mr2" />
              {resetLabel}
            </button>
          )}
        </div>
        <div className="flex-auto flex justify-center ph3">
          {footerCenter}
        </div>
        <div className="flex items-center">
          {onClose && (
            <button
              type="button"
              className="dim tastoInputOrizzontale br3 mr2 inline-flex items-center"
              onClick={onClose}
            >
              <X size={iconSize} className="mr2" />
              {closeLabel}
            </button>
          )}
          {onApply && (
            <button
              type="button"
              className="dim tastoInputOrizzontale br3 inline-flex items-center"
              style={{
                backgroundColor: "var(--colTastoVerde, #137752)",
                color: "#fff",
              }}
              onClick={onApply}
            >
              <Search size={iconSize} className="mr2" />
              {applyLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const getAdvancedSearchOptionValue = (option) => {
  const candidates = [option?.code, option?.idPS, option?.id, option?.value];
  const nonEmpty = candidates.find((item) => item !== undefined && item !== null && String(item).trim() !== "");
  if (nonEmpty !== undefined) return String(nonEmpty).trim();
  return "";
};

export function AdvancedSearchValuePicker({
  label,
  options = [],
  selectedValues = [],
  mode = "in",
  modes = [
    { key: "eq", label: "=" },
    { key: "in", label: "IN" },
    { key: "not_in", label: "NOT IN" },
  ],
  onModeChange,
  onValuesChange,
  tooltip,
  disabled = false,
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [label]);

  const normalizedSelected = selectedValues.map(String);
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => {
      const haystack =
        `${option.description ?? option.label ?? ""} ${getAdvancedSearchOptionValue(option)}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [options, search]);

  const toggleValue = (value) => {
    if (disabled) return;
    const stringValue = String(value);
    if (mode === "eq") {
      onValuesChange?.(
        normalizedSelected.includes(stringValue) ? [] : [stringValue],
      );
      return;
    }
    if (normalizedSelected.includes(stringValue)) {
      onValuesChange?.(
        normalizedSelected.filter((item) => item !== stringValue),
      );
      return;
    }
    onValuesChange?.([...normalizedSelected, stringValue]);
  };

  const handleModeChange = (nextMode) => {
    if (disabled) return;
    const nextValues =
      nextMode === "eq" && normalizedSelected.length > 1
        ? normalizedSelected.slice(0, 1)
        : normalizedSelected;
    onModeChange?.(nextMode, nextValues);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb3">
        <div className="f5 fw6">{label}</div>
        {tooltip && (
          <button
            type="button"
            className="button-reset bg-transparent bn pointer gray"
            title={tooltip}
            aria-label={tooltip}
          >
            <Info size={18} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap mb3">
        {modes.map((item) => {
          const selected = mode === item.key;
          const selectedNotIn = selected && item.key === "not_in";
          return (
            <button
              key={item.key}
              type="button"
              className="button-reset ba br2 pointer mr2 mb2 pv1 ph2 f7"
              style={{
                borderColor: selectedNotIn
                  ? "#b91c1c"
                  : selected
                    ? "var(--colTastoVerde, #137752)"
                    : "#d1d5db",
                backgroundColor: selectedNotIn
                  ? "rgba(185, 28, 28, 0.12)"
                  : selected
                    ? "rgba(19, 119, 82, 0.12)"
                    : "#fff",
                color: selectedNotIn ? "#991b1b" : "#222",
              }}
              onClick={() => handleModeChange(item.key)}
              disabled={disabled}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      <div className="relative mb3">
        <Search
          size={15}
          className="absolute gray"
          style={{ top: 10, left: 10 }}
        />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="input-reset ba b--silver br2 pa2 w-100"
          disabled={disabled}
          style={{ paddingLeft: "2rem", boxSizing: "border-box" }}
          placeholder="Filtra valori"
        />
      </div>
      <div className="overflow-auto" style={{ maxHeight: "48vh" }}>
        {filteredOptions.map((option) => {
          const value = getAdvancedSearchOptionValue(option);
          const checked = normalizedSelected.includes(value);
          return (
            <label
              key={`${label}-${value}`}
              className="flex items-center justify-between pa2 bb b--black-05 pointer"
              style={{
                backgroundColor: checked
                  ? "rgba(19, 119, 82, 0.08)"
                  : undefined,
              }}
            >
              <span>
                <span className="db f6">
                  {option.description ?? option.label ?? value}
                </span>
                <span className="db f7 gray">{value}</span>
              </span>
              <input
                type={mode === "eq" ? "radio" : "checkbox"}
                checked={checked}
                onChange={() => toggleValue(value)}
                disabled={disabled}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
